// Gamepad controls (ui-flow §11.4, M4, optional): left stick target (radial deadzone 0.15),
// A split, B shoot, X special, Start = Esc, LB/RB zoom. Polled once per display frame.
//
// - Uses the first connected pad from `navigator.getGamepads()` (standard mapping indices).
// - Buttons are edge-triggered: one action per press. B also drives `ejectHeld`-style holding
//   through `ejectHeld` (true while B is down), for "Hold W to keep shooting".
// - `connected` fires true/false when a pad appears or disappears (the app shows the
//   "Controller ready" / "Controller unplugged" toasts).
//
// Implementer: client input/UI (M4). Consumer: app.ts.

import { Emitter } from '../net/socket.js';

export interface GamepadEvents {
  action: 'split' | 'eject' | 'special' | 'escape';
  /** One zoom notch: +1 RB (in), −1 LB (out). */
  zoom: { readonly notches: number };
  connected: boolean;
}

/** Radial deadzone of the left stick (ui-flow §11.4). */
export const GAMEPAD_DEADZONE = 0.15;

/** Standard-mapping button indices (ui-flow §11.4). */
export const GAMEPAD_BUTTON = { A: 0, B: 1, X: 2, LB: 4, RB: 5, START: 9 } as const;

/** The parts of the Gamepad API read here (tests pass fakes). */
export interface GamepadLike {
  readonly connected: boolean;
  readonly axes: readonly number[];
  readonly buttons: readonly { readonly pressed: boolean }[];
}

/**
 * Radial deadzone: inside `deadzone` → null; outside, the length is rescaled from
 * [deadzone, 1] to [0, 1] (clamped to 1), keeping the direction.
 */
export function radialDeadzone(
  x: number,
  y: number,
  deadzone = GAMEPAD_DEADZONE,
): { x: number; y: number } | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const len = Math.hypot(x, y);
  if (!(len > deadzone)) return null;
  const scaled = Math.min(1, (len - deadzone) / (1 - deadzone));
  return { x: (x / len) * scaled, y: (y / len) * scaled };
}

function defaultPads(): readonly (GamepadLike | null)[] {
  try {
    const nav = globalThis.navigator as Navigator | undefined;
    return nav?.getGamepads?.() ?? [];
  } catch {
    return [];
  }
}

export class GamepadInput {
  readonly events = new Emitter<GamepadEvents>();
  /** Left stick after the deadzone, length ≤ 1; null when centred or no pad. */
  stick: { x: number; y: number } | null = null;
  /** True while B (shoot) is held. */
  ejectHeld = false;
  private connected = false;
  private readonly prev: boolean[] = [];
  private readonly getPads: () => readonly (GamepadLike | null)[];

  constructor(getPads: () => readonly (GamepadLike | null)[] = defaultPads) {
    this.getPads = getPads;
  }

  /** Reads `navigator.getGamepads()` and emits edge-triggered actions. */
  poll(): void {
    let pad: GamepadLike | null = null;
    for (const p of this.getPads()) {
      if (p && p.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.stick = null;
      this.ejectHeld = false;
      this.prev.length = 0;
      if (this.connected) {
        this.connected = false;
        this.events.emit('connected', false);
      }
      return;
    }
    if (!this.connected) {
      this.connected = true;
      // Buttons already down when the pad appears do not fire.
      this.prev.length = 0;
      for (const b of pad.buttons) this.prev.push(b.pressed);
      this.events.emit('connected', true);
    }
    this.stick = radialDeadzone(pad.axes[0] ?? 0, pad.axes[1] ?? 0);
    this.ejectHeld = pad.buttons[GAMEPAD_BUTTON.B]?.pressed === true;
    if (this.pressed(pad, GAMEPAD_BUTTON.A)) this.events.emit('action', 'split');
    if (this.pressed(pad, GAMEPAD_BUTTON.B)) this.events.emit('action', 'eject');
    if (this.pressed(pad, GAMEPAD_BUTTON.X)) this.events.emit('action', 'special');
    if (this.pressed(pad, GAMEPAD_BUTTON.START)) this.events.emit('action', 'escape');
    if (this.pressed(pad, GAMEPAD_BUTTON.LB)) this.events.emit('zoom', { notches: -1 });
    if (this.pressed(pad, GAMEPAD_BUTTON.RB)) this.events.emit('zoom', { notches: 1 });
    for (let i = 0; i < pad.buttons.length; i++) this.prev[i] = pad.buttons[i]?.pressed === true;
    this.prev.length = pad.buttons.length;
  }

  /** Button `i` went down since the last poll. */
  private pressed(pad: GamepadLike, i: number): boolean {
    return pad.buttons[i]?.pressed === true && this.prev[i] !== true;
  }
}
